import { Info, Loader2 } from 'lucide-react'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { ConversationTranscript } from '@/components/ghl-qualifier/ConversationTranscript'
import { useToast } from '@/hooks/use-toast'
import { useDeleteModelLabLabel, useModelLabCaseDetail, useSaveModelLabLabel } from '@/hooks/useModelLab'
import { formatDecision, TASK_META, type LabelValue, type ModelLabLabel } from '@/lib/model-lab'
import { LabelControls } from './LabelControls'
import { ResultCard } from './ResultCard'

interface Props {
  readonly caseId: string | null
  readonly labels: ReadonlyMap<string, ModelLabLabel>
  readonly onClose: () => void
}

const FIDELITY_NOTE: Readonly<Record<string, string>> = {
  analysis: "Rebuilt from the stored transcript. The opportunity's existing custom fields and stage weren't stored, so every model sees the prompt without them.",
  grace: 'Transcript is the latest one the middleware analysed before the grace period ended.',
  gate: 'Transcript is the latest one the middleware analysed before this gate ran; later operator texts may be missing.',
  sms: 'Trip details and price come from the analyses stored before this text was sent, not a live GHL read.',
}

function ContextList({ entries }: { readonly entries: ReadonlyArray<[string, unknown]> }) {
  const shown = entries.filter(([, v]) => v !== null && v !== undefined && v !== '')
  if (shown.length === 0) return null
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs">
      {shown.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="text-muted-foreground">{k}</dt>
          <dd>{String(v)}</dd>
        </div>
      ))}
    </dl>
  )
}

export function CaseDrawer({ caseId, labels, onClose }: Props) {
  const { toast } = useToast()
  const detail = useModelLabCaseDetail(caseId)
  const saveLabel = useSaveModelLabLabel()
  const deleteLabel = useDeleteModelLabLabel()

  const labCase = detail.data?.labCase
  const results = detail.data?.results ?? []
  const stored = labCase ? labels.get(labCase.case_key) : undefined
  const input = (labCase?.input_snapshot ?? {}) as Record<string, unknown>
  const transcript = (input.messages ?? input.transcript ?? []) as unknown[]
  const dueRows = (input.dueRows ?? []) as Array<{ id: string; step_label: string }>
  const saving = saveLabel.isPending || deleteLabel.isPending

  const onError = (error: unknown) =>
    toast({ title: 'Could not save label', description: error instanceof Error ? error.message : String(error), variant: 'destructive' })

  const save = (label: LabelValue, notes: string | null) => {
    if (!labCase) return
    saveLabel.mutate(
      { task: labCase.task, caseKey: labCase.case_key, locationId: labCase.location_id, label, notes },
      { onError }
    )
  }

  const rate = (contestantKey: string, rating: 'good' | 'bad') =>
    save({ ratings: { ...(stored?.label.ratings ?? {}), [contestantKey]: rating } }, stored?.notes ?? null)

  const accept = (decision: string) => save({ ...(stored?.label ?? {}), decision }, stored?.notes ?? null)

  const clear = () => {
    if (!labCase) return
    deleteLabel.mutate({ task: labCase.task, caseKey: labCase.case_key, locationId: labCase.location_id }, { onError })
  }

  const context = input.context as Record<string, unknown> | undefined
  const production = labCase?.production_output

  return (
    <Sheet open={caseId !== null} onOpenChange={(open) => !open && onClose()}>
      {/* Near full-screen: side-by-side model answers need the room. */}
      <SheetContent side="right" className="w-[96vw] max-w-none overflow-y-auto sm:max-w-none">
        {detail.isLoading || !labCase ? (
          <div className="flex h-40 items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-4">
            <SheetHeader>
              <SheetTitle>{TASK_META[labCase.task].label}</SheetTitle>
              <SheetDescription className="font-mono text-xs">
                {labCase.case_key}
                {labCase.source_created_at && ` · ${new Date(labCase.source_created_at).toLocaleString()}`}
              </SheetDescription>
            </SheetHeader>

            <p className="flex gap-2 rounded-md bg-amber-50 p-2 text-xs text-amber-900">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              {FIDELITY_NOTE[labCase.task]}
            </p>

            {/* minmax(0, …) lets the columns shrink below their content instead of overflowing. */}
            <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
              <div className="min-w-0 space-y-3">
                <LabelControls
                  key={`${labCase.case_key}:${stored?.updated_at ?? 'unlabelled'}`}
                  task={labCase.task}
                  label={stored?.label}
                  notes={stored?.notes ?? null}
                  dueRows={dueRows}
                  saving={saving}
                  onSave={save}
                  onClear={clear}
                />

                <div className="rounded-md border p-3 text-xs">
                  <p className="mb-1 font-medium">Production decided (reference)</p>
                  {labCase.task === 'sms' ? (
                    <p className="rounded bg-muted p-2 text-sm">{String(production?.message ?? '—')}</p>
                  ) : (
                    <p>
                      {formatDecision(production?.decision)}
                      {production?.model ? <span className="text-muted-foreground"> · {String(production.model)}</span> : null}
                    </p>
                  )}
                  {typeof production?.reasoning === 'string' && <p className="mt-1 text-muted-foreground">{production.reasoning}</p>}
                </div>

                {context && (
                  <ContextList
                    entries={[
                      ['Contact', context.contactName],
                      ['Opportunity', context.existingOpportunityId],
                      ['"Today"', input.today],
                    ]}
                  />
                )}
                {labCase.task === 'sms' && (
                  <ContextList
                    entries={[
                      ['Step', input.stepLabel],
                      ['Customer', input.contactFirstName],
                      ['Vehicle', input.vehicleType],
                      ['Quote', input.monetaryValue],
                      ['Pickup', [input.pickupDate, input.pickupTime].filter(Boolean).join(' ')],
                      ['From', input.pickupAddress],
                      ['To', input.dropoffAddress],
                    ]}
                  />
                )}
                {Array.isArray(input.previousFollowupMessages) && input.previousFollowupMessages.length > 0 && (
                  <div className="text-xs">
                    <p className="mb-1 font-medium">Follow-ups already sent</p>
                    <ul className="list-disc space-y-0.5 pl-4 text-muted-foreground">
                      {(input.previousFollowupMessages as string[]).map((m, i) => <li key={i}>{m}</li>)}
                    </ul>
                  </div>
                )}

                <ConversationTranscript rawMessages={transcript} />
              </div>

              <div className="min-w-0 space-y-3">
                <p className="text-sm font-medium">Model answers ({results.length})</p>
                {results.length === 0 && <p className="text-sm text-muted-foreground">No model has answered yet.</p>}
                {/* As many 340px+ columns as fit, so answers sit side by side on wide screens. */}
                <div className="grid gap-3 [grid-template-columns:repeat(auto-fill,minmax(340px,1fr))]">
                {results.map((result) => (
                  <ResultCard
                    key={result.id}
                    task={labCase.task}
                    result={result}
                    label={stored?.label}
                    saving={saving}
                    onAccept={accept}
                    onRate={(rating) => rate(result.contestant_key, rating)}
                  />
                ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}
